import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, Loader2, CheckCircle2, FileText, Users } from "lucide-react";
import { toast } from "sonner";


function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoNDaysAgo(n) {
    const d = new Date(); d.setUTCDate(d.getUTCDate() - (n - 1));
    return d.toISOString().slice(0, 10);
}

/**
 * Multi-purpose CSV export dialog.
 *
 * report:
 *   - "students"  → /api/admin/students/export.csv with columns chips
 *   - "loa"       → /api/admin/loa-history/export.csv with document_type filter
 */
export default function ExportCsvDialog({
    open, onOpenChange,
    report = "students",  // "students" | "loa"
    programs = [],        // for students export filter
}) {
    const [loading, setLoading] = useState(false);
    const [columns, setColumns] = useState([]);     // available columns (for students)
    const [selected, setSelected] = useState(new Set());
    const [startDate, setStartDate] = useState(isoNDaysAgo(90));
    const [endDate, setEndDate] = useState(isoToday());
    const [programId, setProgramId] = useState("__all");
    const [docType, setDocType] = useState("__all"); // for LOA report

    useEffect(() => {
        if (!open || report !== "students") return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/reports/columns");
                if (cancelled) return;
                const cols = data?.students || [];
                setColumns(cols);
                if (selected.size === 0) {
                    // Sensible defaults — keep the export readable.
                    setSelected(new Set([
                        "student_id", "name", "email", "phone", "country",
                        "campus", "admission_term", "enrolled_programs", "created_at",
                    ]));
                }
            } catch {
                toast.error("Could not load export columns.");
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, report]);

    const toggle = (key) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const selectAll = () => setSelected(new Set(columns.map((c) => c.key)));
    const clearAll = () => setSelected(new Set());

    const filename = useMemo(() => {
        if (report === "students") return `students-${startDate}-to-${endDate}.csv`;
        return `loa-history-${startDate}-to-${endDate}.csv`;
    }, [report, startDate, endDate]);

    const download = async () => {
        setLoading(true);
        try {
            let url;
            const params = { start: startDate, end: endDate };
            if (report === "students") {
                if (selected.size === 0) {
                    toast.error("Pick at least one column.");
                    return;
                }
                url = "/admin/students/export.csv";
                params.columns = Array.from(selected).join(",");
                if (programId && programId !== "__all") params.program_id = programId;
            } else {
                url = "/admin/loa-history/export.csv";
                if (docType && docType !== "__all") params.document_type = docType;
            }
            const resp = await api.get(url, { params, responseType: "blob" });
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
            toast.success("CSV downloaded.");
            onOpenChange?.(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Export failed.");
        } finally {
            setLoading(false);
        }
    };

    const title = report === "loa" ? "Export LOA / COL history" : "Export student data";
    const Icon = report === "loa" ? FileText : Users;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[88vh] overflow-y-auto"
                data-testid="export-csv-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-xl">
                        <Icon className="h-5 w-5 text-primary" />
                        {title}
                    </DialogTitle>
                    <DialogDescription>
                        Pick the columns you need and the date range. The file opens in Excel
                        with a UTF-8 BOM so accents render correctly.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <div>
                        <Label className="text-xs text-muted-foreground">Start date</Label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            data-testid="export-start-date"
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">End date</Label>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            data-testid="export-end-date"
                        />
                    </div>

                    {report === "students" && programs.length ? (
                        <div className="sm:col-span-2">
                            <Label className="text-xs text-muted-foreground">Program filter</Label>
                            <Select value={programId} onValueChange={setProgramId}>
                                <SelectTrigger data-testid="export-program-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all">All programs</SelectItem>
                                    {programs.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    ) : null}

                    {report === "loa" ? (
                        <div className="sm:col-span-2">
                            <Label className="text-xs text-muted-foreground">Document type</Label>
                            <Select value={docType} onValueChange={setDocType}>
                                <SelectTrigger data-testid="export-doc-type-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all">All (LOA + COL)</SelectItem>
                                    <SelectItem value="loa">LOA only</SelectItem>
                                    <SelectItem value="col">COL only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    ) : null}
                </div>

                {report === "students" && columns.length ? (
                    <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs text-muted-foreground font-semibold">
                                Columns ({selected.size} / {columns.length})
                            </Label>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={selectAll}
                                    data-testid="export-cols-select-all"
                                >
                                    Select all
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={clearAll}
                                    data-testid="export-cols-clear"
                                >
                                    Clear
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="export-cols-chips">
                            {columns.map((c) => {
                                const on = selected.has(c.key);
                                return (
                                    <button
                                        key={c.key}
                                        type="button"
                                        onClick={() => toggle(c.key)}
                                        className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                                            on
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-background text-muted-foreground border-border hover:border-primary/40"
                                        }`}
                                        data-testid={`export-col-${c.key}`}
                                    >
                                        {on ? <CheckCircle2 className="inline h-3 w-3 mr-1" /> : null}
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>
                        {selected.has("sin_masked") ? (
                            <p className="text-[11px] text-amber-700 mt-2">
                                <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 mr-1.5">
                                    Sensitive
                                </Badge>
                                SIN values are exported masked (XXX-XXX-NNN) — the full value
                                is never written to disk.
                            </p>
                        ) : null}
                    </div>
                ) : null}

                <div className="flex justify-end gap-2 pt-4 mt-3 border-t border-border">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange?.(false)}
                        disabled={loading}
                        data-testid="export-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={download}
                        disabled={loading || (report === "students" && selected.size === 0)}
                        className="gap-1.5"
                        data-testid="export-download-btn"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Download CSV
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
